'use client';

import { useMemo } from 'react';
import type { Section, FormInstance, FormValues } from '../types';
import { evaluateConditions } from '../utils/conditions';
import { DictionaryT, getSectionStatus } from '../utils/sectionStatus';
import { mergeFormValues } from '../utils/valueMerger';
import { isCardConsideredCompleted } from '../utils/cardUtils';

interface UseSectionVisibilityProps {
  sections: Section[];
  values: FormValues;
  completedForms: Record<string, FormInstance[]>;
  currentSectionId?: string | null;
  localStorageKey?: string;
  getDictionaryValue?: DictionaryT;
}

interface UseSectionVisibilityReturn {
  visibleSections: Section[];
  firstIncompleteIndex: string | number;
  allSectionsCompleted: boolean;
}

/**
 * Hook to calculate section visibility and first incomplete index
 */
export const useSectionVisibility = ({
  sections,
  values,
  completedForms,
  currentSectionId = null,
  localStorageKey,
  getDictionaryValue,
}: UseSectionVisibilityProps): UseSectionVisibilityReturn => {
  // Merge form-level values to ensure all conditions can be evaluated
  const mergedValues = useMemo(() => {
    return mergeFormValues(values, completedForms, localStorageKey, true) as FormValues;
  }, [values, completedForms, localStorageKey]);

  const visibleSections = useMemo(() => {
    return sections.filter((section) => evaluateConditions(section.showIf, mergedValues));
  }, [sections, mergedValues]);

  // Determine first incomplete section/card index (for CTA enable/disable)
  const firstIncompleteIndex = useMemo(() => {
    for (let sectionIndex = 0; sectionIndex < visibleSections.length; sectionIndex++) {
      const section = visibleSections[sectionIndex];

      // Check if section has cards
      if (section.cards && section.cards.length > 0) {
        const visibleCards = section.cards.filter((card) =>
          evaluateConditions(card.showIf, mergedValues)
        );
        for (let cardIndex = 0; cardIndex < visibleCards.length; cardIndex++) {
          const card = visibleCards[cardIndex];

          // Check if card should be considered completed based on configuration
          if (isCardConsideredCompleted(card, mergedValues)) {
            continue; // Skip this card, it's not required
          }

          const completedInstances = completedForms[card.groupKey] || [];
          if (completedInstances.length === 0) {
            // Return a unique identifier for this card
            return `${sectionIndex}-${cardIndex}`;
          }
        }
      } else {
        // Legacy: section without cards
        const groupKey = section.groupKey || section.id;
        const completedInstances = completedForms[groupKey] || [];
        if (completedInstances.length === 0) {
          return sectionIndex;
        }
      }
    }
    return -1;
  }, [visibleSections, completedForms, mergedValues]);

  // Check if all sections are completed
  const allSectionsCompleted = useMemo(() => {
    if (visibleSections.length === 0) return false;

    return visibleSections.every((section) => {
      const status = getSectionStatus(
        section,
        completedForms,
        currentSectionId,
        mergedValues,
        localStorageKey,
        getDictionaryValue
      );
      return status === (getDictionaryValue && getDictionaryValue('Complete', 'Complete'));
    });
  }, [
    visibleSections,
    completedForms,
    currentSectionId,
    mergedValues,
    localStorageKey,
    getDictionaryValue,
  ]);

  return {
    visibleSections,
    firstIncompleteIndex,
    allSectionsCompleted,
  };
};
